<template>
  <div class="flex">
    <!-- Main table content -->
    <div class="flex flex-col border border-gray-300 bg-white shadow-lg rounded-lg h-[45rem] mx-auto relative mt-6">
      <h1>
        <div class="flex flex-col items-center">
          <span class="text-2xl font-thin">Subscriptions</span>
          <span class="text-base text-gray-400">({{ store.tableState.nevuaSubscriptions.length }} active alerts)</span>
        </div>
      </h1>

      <div v-if="loading" class="absolute inset-0 flex justify-center items-center z-20">
        <div class="absolute inset-0 bg-white bg-opacity-50 backdrop-blur-sm"></div>
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 z-30"></div>
      </div>

      <div class="flex p-4">
        <div class="flex space-x-2">
          <button 
            @click="pauseAllAlerts"
            class="flex items-center justify-center border bg-white shadow-sm hover:bg-gray-100 rounded-md px-3 text-xs h-8" 
            type="button"
          >
            <i class="fas fa-pause mr-2"></i>
            Pause All Alerts
          </button>
          
          <button 
            @click="restartAllAlerts"
            class="flex items-center justify-center border bg-white shadow-sm hover:bg-gray-100 rounded-md px-3 text-xs h-8" 
            type="button"
          >
            <i class="fas fa-play mr-2"></i>
            Restart All Alerts
          </button>

          <button 
            @click="deleteAllAlerts"
            class="flex items-center justify-center border bg-white shadow-sm hover:bg-red-50 rounded-md px-3 text-xs h-8 text-red-800" 
            type="button"
          >
            <i class="fas fa-trash-alt mr-2"></i>
            Delete All Alerts
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-scroll sticky top-0">
        <table class="w-full divide-y divide-gray-200 font-mono text-xs table-fixed">
          <thead class="bg-gray-50 sticky top-0 z-10">
            <tr class="h-max">
              <th v-for="column in columns" 
                  :key="column.key" 
                  scope="col" 
                  :class="['first:pl-4 h-8', getColumnWidthClass(column.key)]">
                <div class="flex items-center">
                  <span class="th-class">{{ column.label }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="subscription in store.tableState.nevuaSubscriptions" 
                :key="subscription.subscriptionId"
                class="h-12 hover:bg-gray-100">
              <td v-for="column in columns" 
                  :key="column.key"
                  :class="['mr-6 first:pl-4', getColumnWidthClass(column.key), column.key == 'lastTriggeredAt' ? 'text-center' : '']">
                <div class="text-xs">
                  <div v-if="column.key === 'scope'">
                    <span class="p-1">
                      {{ subscription.subscriptionScope }}
                    </span>
                  </div>
                  <div v-else-if="column.key === 'name'">
                    <div>
                      <span>
                        <span v-html="getColumnValue(subscription, column.key)" class="mr-2"></span>
                        <a v-if="subscription.subscriptionScope !== SubscriptionScope.WATCHLIST" href="#" @click.prevent="openWatchlist(subscription.watchlistId)" class="p-1 rounded-md bg-gray-100 text-gray-600 whitespace-nowrap">{{ getWatchlistName(subscription.watchlistId) }}</a>
                      </span>
                    </div>
                  </div>
                  <div v-else-if="column.key === 'actions'" class="flex space-x-2">
                    <button 
                      @click="subscription.status.enabled ? pauseAlert(subscription) : startAlert(subscription)" 
                      :class="[
                        'p-1.5 rounded-md',
                        subscription.status.enabled ? 'hover:bg-gray-200' : 'hover:bg-gray-200',
                        !subscription.status.enabled && !canStartAlert(subscription) ? 'opacity-50 cursor-not-allowed' : ''
                      ]"
                      :disabled="!subscription.status.enabled && !canStartAlert(subscription)"
                      v-tippy="{ content: getAlertToggleButtonContent(subscription), delay: [300, 0], allowHTML: true }"
                    >
                      <i :class="[
                        subscription.status.enabled ? 'fas fa-pause' : 'fas fa-play',
                        !subscription.status.enabled && !canStartAlert(subscription) ? 'opacity-80' : ''
                      ]"></i>
                    </button>
                    <button 
                      @click="editAlert(subscription)" 
                      class="p-1.5 hover:bg-gray-200 rounded-md"
                      title="Edit Alert"
                      v-tippy="{ content: 'Edit Alert', delay: [300, 0] }"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      @click="handleDelete(subscription.subscriptionId)" 
                      class="p-1.5 hover:bg-gray-200 rounded-md text-red-600"
                      title="Delete Alert"
                      v-tippy="{ content: 'Delete Alert', delay: [300, 0] }"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                  <div v-else>
                    <span v-html="getColumnValue(subscription, column.key)"></span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <TransitionRoot appear :show="isDeleteModalOpen" as="template">
      <Dialog as="div" @close="closeDeleteModal" class="relative z-50">
        <div class="fixed inset-0 bg-black/30" />
        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6">
              <DialogTitle class="text-lg font-medium mb-4">Confirm Deletion</DialogTitle>
              <p class="mb-6">
                <template v-if="deletionMode === 'all'">
                  Are you sure you want to delete all alerts? This action cannot be undone.
                </template>
                <template v-else>
                  Are you sure you want to delete this alert? This action cannot be undone.
                </template>
              </p>
              <div class="flex justify-end space-x-3">
                <button 
                  @click="closeDeleteModal"
                  class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button 
                  @click="confirmDelete"
                  class="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white"
                >
                  Delete
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Subscription Dialog Panel -->
    <div v-show="showSubscriptionsDialog" class="w-[500px] border-l border-gray-200 mt-6">
      <SubscriptionsDialogContainer
        ref="subscriptionsDialog"
        :is-open="showSubscriptionsDialog"
        :is-mobile="false"
        :scope="subscriptionScope"
        :target-title="subscriptionTargetTitle"
        :subscription-to-edit="subscriptionToEdit"
        :is-edit="!!subscriptionToEdit"
        @close="handleCloseSubscriptionsDialog"
        @delete="handleDelete"
        @edit="handleEdit"
        @confirm-delete="handleConfirmDelete"
      />
    </div>
  </div>
</template>

<script>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from '@headlessui/vue';
import apiClient from '../utils/axios';
import { formatDistanceToNowStrict } from 'date-fns';
import { showDialog, store, fetchSubscriptionsToGlobalState } from '../store';
import { SubscriptionScope, TriggerType, SubscriptionStatusReason } from '../types/Subscriptions';
import _ from 'lodash';
import SubscriptionsDialogContainer from './SubscriptionsDialogContainer.vue';

export default {
  name: 'SubscriptionsTable',
  components: {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    SubscriptionsDialogContainer,
  },
  
  data() {
    return {
      store,
      SubscriptionScope,
      loading: true,
      isDeleteModalOpen: false,
      subscriptionIdToDelete: null,
      deletionMode: null, // 'single' | 'all' | null
      showSubscriptionsDialog: false,
      subscriptionScope: null,
      subscriptionTargetTitle: '',
      subscriptionToEdit: null,
      columns: [
        { key: 'name', label: 'Name', width: 'w-40' },
        { key: 'scope', label: 'Scope', width: 'w-20' },
        { key: 'triggerType', label: 'Trigger', width: 'w-16' },
        { key: 'triggerCount', label: 'Count', width: 'w-10' },
        { key: 'lastTriggeredAt', label: 'Last Triggered', width: 'w-24' },
        { key: 'status', label: 'Status', width: 'w-20' },
        { key: 'expiresAt', label: 'Expires', width: 'w-20' },
        { key: 'type', label: 'Type', width: 'w-20' },
        { key: 'config', label: 'Config', width: 'w-10' },
        { key: 'channels', label: 'Channels', width: 'w-20' },
        { key: 'actions', label: 'Actions', width: 'w-20' },
      ]
    };
  },

  mounted() {
    this.fetchSubscriptions();
  },

  methods: {
    getWatchlistName(watchlistId) {
      return store.watchLists.find(watchlist => watchlist.id === watchlistId)?.name || 'N/A';
    },

    openWatchlist(watchlistId) {
      this.$router.push(`/l/${watchlistId}`);
    },

    size(obj) {
      return _.size(obj);
    },

    async fetchSubscriptions() {
      try {
        await fetchSubscriptionsToGlobalState();
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      } finally {
        this.loading = false;
      }
    },

    async pauseAllAlerts() {
      try {
        let response = await apiClient.post('https://api.nevua.markets/subscriptions/pause-all');
        if (response.status !== 200) {
          throw new Error('Failed to pause alerts');
        }
        showDialog({
          message: 'All alerts have been paused',
          type: 'success',
          timeout: 3000
        });

        await this.fetchSubscriptions();
      } catch (error) {
        showDialog({
          message: 'Failed to pause alerts',
          type: 'error',
          timeout: 3000
        });
        return;
      }
    },

    async restartAllAlerts() {
      try {
        let response = await apiClient.post('https://api.nevua.markets/subscriptions/restart-all');
        if (response.status !== 200) {
          throw new Error('Failed to restart alerts');
        }
        showDialog({
          message: 'All alerts have been restarted',
          type: 'success',
          timeout: 3000
        });

        await this.fetchSubscriptions();
      } catch (error) {
        showDialog({
          message: 'Failed to restart alerts',
          type: 'error',
          timeout: 3000
        });
        return;
      }
    },

    async deleteAllAlerts() {
      this.deletionMode = 'all';
      this.isDeleteModalOpen = true;
    },

    handleDelete(subscriptionId) {
      this.deletionMode = 'single';
      this.subscriptionIdToDelete = subscriptionId;
      this.isDeleteModalOpen = true;
    },

    closeDeleteModal() {
      this.isDeleteModalOpen = false;
      this.subscriptionIdToDelete = null;
      this.deletionMode = null;
    },

    async confirmDelete() {
      try {
        if (this.deletionMode === 'single' && this.subscriptionIdToDelete) {
          const response = await apiClient.delete(`https://api.nevua.markets/subscriptions/${this.subscriptionIdToDelete}`);
          if (response.status !== 200) {
            throw new Error('Failed to delete alert');
          }
          showDialog({
            message: 'Alert deleted successfully',
            type: 'success',
            timeout: 3000
          });

          await this.fetchSubscriptions();

        } else if (this.deletionMode === 'all') {
          const response = await apiClient.delete('https://api.nevua.markets/subscriptions/delete-all');
          if (response.status === 200) {
            showDialog({
              message: 'All alerts have been deleted',
              type: 'success',
              timeout: 3000
            });
            
            await this.fetchSubscriptions();
          }
        } else {
          throw new Error('Invalid deletion mode');
        }
      } catch (error) {
        showDialog({
          message: 'Failed to delete alert(s)',
          type: 'error',
          timeout: 3000
        });
      } finally {
        this.handleCloseSubscriptionsDialog();
        this.closeDeleteModal();
      }
    },

    async pauseAlert(subscription) {
      try {
        const response = await apiClient.post(`https://api.nevua.markets/subscriptions/${subscription.subscriptionId}/pause`);
        if (response.status !== 200) {
          throw new Error('Failed to pause alert')
        }
        showDialog({
          message: 'Alert paused successfully',
          type: 'success',
          timeout: 3000
        });

        // Update subscriptions table
        await this.fetchSubscriptions();
      } catch (error) {
        showDialog({
          message: 'Failed to pause alert',
          type: 'error',
          timeout: 3000
        });
      }
    },

    getStartAlertContent(subscription) {
      let disabledReasons = []

      // if (subscription.status.enabled) {
      //   return 'Alert is already enabled'
      // }

      if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
        disabledReasons.push('- Event closed')
      }

      if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
        disabledReasons.push('- Market closed')
      }

      if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
        disabledReasons.push('- Alert expired')
      }

      if (subscription.channels.length === 0) {
        disabledReasons.push('- No notification channels configured')
      }

      // if (subscription.triggerCount && subscription.triggerCount >= 1 && subscription.triggerType === TriggerType.ONE_TIME) {
      //   disabledReasons.push('- Trigger limit reached')
      // }

      if (disabledReasons.length > 0) {
        return 'Cannot start alert. Reasons: ' + disabledReasons.join('<br>')
      }

      return 'Start Alert'
    },

    async startAlert(subscription) {
      try {
        const response = await apiClient.post(`https://api.nevua.markets/subscriptions/${subscription.subscriptionId}/restart`);
        if (response.status !== 200) {
          throw new Error('Failed to start alert')
        }
        showDialog({
          message: 'Alert started successfully',
          type: 'success',
          timeout: 3000
        });

        // Update subscriptions table
        await this.fetchSubscriptions();
      } catch (error) {
        showDialog({
          message: 'Failed to start alert',
          type: 'error',
          timeout: 3000
        });
      }
    },

    editAlert(subscription) {
      this.subscriptionToEdit = subscription;
      this.subscriptionScope = subscription.subscriptionScope;
      this.subscriptionTargetTitle = this.getSubscriptionTitle(subscription);
      this.showSubscriptionsDialog = true;
    },

    getSubscriptionTitle(subscription) {
      switch (subscription.subscriptionScope) {
        case SubscriptionScope.WATCHLIST:
          return this.getWatchlistName(subscription.watchlistId);
        case SubscriptionScope.EVENT:
          return subscription.eventTitle;
        case SubscriptionScope.MARKET:
          return subscription.marketQuestion;
        default:
          return 'N/A';
      }
    },

    handleCloseSubscriptionsDialog() {
      this.showSubscriptionsDialog = false;

      this.subscriptionScope = null;
      this.subscriptionToEdit = null;
      this.subscriptionTargetTitle = null;
      this.eventsToCreateAlertFor = null;
      this.listAlertsForMarketId = null;
      this.listAlertsForEventId = null;
      this.subscriptionIdToDelete = null;
      this.showAlertListTabFirst = false;
      //this.isSubscriptionDeleteModalOpen = false;
    },

    getColumnWidthClass(columnKey) {
      const column = this.columns.find(col => col.key === columnKey);
      return column?.width || 'w-32';
    },

    formatDistanceToNow(dateString) {
      if (!dateString) return '';
      try {
        return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true });
      } catch (error) {
        console.warn(`Invalid date format for: ${dateString}`);
        return '';
      }
    },

    getColumnValue(subscription, key) {
      switch (key) {
        case 'name':
          switch (subscription.subscriptionScope) {
            case SubscriptionScope.WATCHLIST:
              return `<span>${subscription.watchlistName || 'N/A'}</span>`;
            case SubscriptionScope.EVENT:
              return `<span>${subscription.eventTitle || 'N/A'}</span>`;
            case SubscriptionScope.MARKET:
              return `<span>${subscription.marketQuestion || 'N/A'}</span>`;
            default:
              return `<span>'N/A'</span>`;
          }
        case 'scope':
          return subscription.subscriptionScope;
        case 'triggerType':
          return subscription.triggerType === TriggerType.ONE_TIME ? 'One-time' : 'Recurring';
        case 'triggerCount':
          return subscription.triggerCount || '0';
        case 'lastTriggeredAt':
          return subscription.lastTriggeredAt ? this.formatDistanceToNow(subscription.lastTriggeredAt) : 'Never';
        case 'status':
          if (subscription.status.enabled) {
            return `
              <span 
                class="ml-2 border border-green-500 rounded-md px-2 py-1"
                title="${subscription.status.reason}">Active</span>
            `;
          } else {
            return `
                <span 
                class="ml-2 border border-red-500 rounded-md px-2 py-1"
                title="${subscription.status.reason}">Inactive</span>
            `;
          }
        case 'expiresAt':
          return this.formatDistanceToNow(subscription.expiresAt);
        case 'config':
          // let params = [];
          // for (const [key, value] of Object.entries(subscription.subscriptionTypeConfig.config.params)) {
          //   params.push(`${key}: ${value}`);
          // }
          // return params.join(', ');
          if (subscription.subscriptionTypeConfig.config?.params?.threshold_percent) {
            return `${subscription.subscriptionTypeConfig.config.params.threshold_percent}%`;
          } else {
            return '';
          }
        case 'type':
          return subscription.subscriptionTypeConfig.type;
        case 'channels':
          return subscription.channels.map(channel => channel.type).join(', ');
        default:
          return '';
      }
    },

    canStartAlert(subscription) {
      if (subscription.status.enabled) {
        return true;
      }

      if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
        return false;
      }

      if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
        return false;
      }

      if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
        return false;
      }

      if (subscription.channels.length === 0) {
        return false;
      }

      return true;
    },

    getAlertToggleButtonContent(subscription) {
      if (subscription.status.enabled) {
        return 'Pause Alert';
      }

      let disabledReasons = [];

      if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
        disabledReasons.push('- Event closed');
      }

      if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
        disabledReasons.push('- Market closed');
      }

      if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
        disabledReasons.push('- Alert expired');
      }

      if (subscription.channels.length === 0) {
        disabledReasons.push('- No channels configured');
      }

      if (disabledReasons.length > 0) {
        return 'Cannot start alert. Reasons: ' + disabledReasons.join('<br>');
      }

      return 'Start Alert';
    },

    handleEdit(subscription) {
      this.subscriptionToEdit = subscription;
    },

    async handleConfirmDelete(subscription) {
      try {
        const response = await apiClient.delete(`/subscriptions/${subscription.subscriptionId}`);
        if (response.status === 200) {
          showDialog({
            message: 'Alert deleted successfully',
            type: 'success',
            timeout: 3000
          });
          await this.fetchSubscriptions();
          this.handleCloseSubscriptionsDialog();
        }
      } catch (error) {
        showDialog({
          message: 'Failed to delete alert',
          type: 'error',
          timeout: 3000
        });
      }
    },
  }
};
</script>

<style scoped>
.th-class {
  @apply text-center text-sm font-medium text-gray-500 uppercase tracking-wider;
}

thead, tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

thead {
  width: calc(100% - 8px); /* Subtract scrollbar width */
}
</style> 